import { object, string, number, boolean, mixed } from 'yup'
import {
  RULE_ACTIONS,
  RULE_TYPES,
  GEOGRAPHIC_CRITERIA_TYPES,
  GEOGRAPHIC_DIRECTIONS,
  MILEAGE_BUCKETS,
  DATE_TYPES,
} from '../types/shipperStrikePrices'
import { PARENT_EQUIPMENT_KEY, DAY_OR_HOUR } from '../types/spotBiddingConfig'

export interface IStrikePriceRuleSchema {
  reason: string
  percentage: number
  ruleAction: RULE_ACTIONS
  ruleType: RULE_TYPES
  geographicCriteriaType: GEOGRAPHIC_CRITERIA_TYPES
  geographicCriteriaDirection: GEOGRAPHIC_DIRECTIONS
  laneGeographicCriteriaPickupType: GEOGRAPHIC_CRITERIA_TYPES
  laneGeographicCriteriaDeliveryType: GEOGRAPHIC_CRITERIA_TYPES
  laneGeographicCriteriaPickupValue: string
  laneGeographicCriteriaDeliveryValue: string
  geographicCriteriaValue: string
  dateType: DATE_TYPES
  dateCriteria?: {
    rangeStart?: string
    rangeEnd?: string
  }
  proximalHourCriteria?: {
    rangeStart?: number
    rangeEnd?: number
    dayOrHour?: DAY_OR_HOUR
  }
  ruleStartTimeCriteria?: {
    rangeStart?: string
  }
  includeGeo: boolean
  mileageBucket?: MILEAGE_BUCKETS
  parentEquipmentKey?: PARENT_EQUIPMENT_KEY
  appointmentType?: string
  appointmentTime: boolean
  appointmentTimeCriteria?: {
    appointmentTimeCriteria?: {
      deliveryRangeEnd?: string
      deliveryRangeStart?: string
      pickupRangeEnd?: string
      pickupRangeStart?: string
    }
  }
  countLoadsQuotedCriteria?: {
    pickupType: string
    deliveryType: string
    pickupValue: string
    deliveryValue: string
    rangeStart?: number
    rangeEnd?: number
    isSamePickupDelivery?: boolean
    isDifferentPickupDelivery?: boolean
  }
}

export const StrikePriceRuleSchema = object<IStrikePriceRuleSchema>({
  reason: string().required('Must enter a reason'),
  percentage: number()
    .typeError('Must be a number')
    .when('ruleType', {
      is: RULE_ACTIONS.APPLY_PREMIUM,
      then: number().required('Must specify a percentage'),
    }),
  ruleAction: mixed().oneOf(Object.keys(RULE_ACTIONS)),
  ruleType: mixed(),
  dateType: mixed<DATE_TYPES>().oneOf(Object.values(DATE_TYPES)),
  geographicCriteriaType: mixed().oneOf(Object.values(GEOGRAPHIC_CRITERIA_TYPES)),
  geographicCriteriaDirection: mixed().oneOf(Object.values(GEOGRAPHIC_DIRECTIONS)),
  geographicCriteriaValue: string().when(['ruleType', 'includeGeo'], {
    is: (ruleType, includeGeo) =>
      ruleType === RULE_TYPES.GEOGRAPHIC || (ruleType === RULE_TYPES.MILEAGE && includeGeo),
    then: string().required('Must specify a location'),
  }),
  laneGeographicCriteriaPickupType: mixed().oneOf(Object.values(GEOGRAPHIC_CRITERIA_TYPES)),
  laneGeographicCriteriaDeliveryType: mixed().oneOf(Object.values(GEOGRAPHIC_CRITERIA_TYPES)),
  laneGeographicCriteriaPickupValue: string().when(['ruleType'], {
    is: ruleType => ruleType === RULE_TYPES.LANE,
    then: string().required('Must specify a location'),
  }),
  laneGeographicCriteriaDeliveryValue: string().when(['ruleType'], {
    is: ruleType => ruleType === RULE_TYPES.LANE,
    then: string().required('Must specify a location'),
  }),
  dateCriteria: object().when('dateType', {
    is: DATE_TYPES.DATE_RANGE,
    then: object().shape({
      rangeStart: string().required('Must enter a date range'),
      rangeEnd: string(),
    }),
    otherwise: object().notRequired(),
  }),
  proximalHourCriteria: object().when('dateType', {
    is: DATE_TYPES.NUMBER_OF_DAYS_OUT,
    then: object().typeError('Must enter a number of days out'),
  }),
  includeGeo: boolean(),
  appointmentTime: boolean(),
  appointmentType: string().when(['dateType', 'appointmentTime'], {
    is: (dateType, appointmentTime) => DATE_TYPES.NUMBER_OF_DAYS_OUT && appointmentTime,
    then: string().required('Enter Appointment Type'),
  }),
  appointmentTimeCriteria: object().when(['dateType', 'appointmentTime'], {
    is: (dateType, appointmentTime) => DATE_TYPES.NUMBER_OF_DAYS_OUT && appointmentTime,
    then: object().typeError('Must enter appointment time'),
  }),
  mileageBucket: mixed().oneOf(Object.values(MILEAGE_BUCKETS)),
  parentEquipmentKey: mixed()
    .oneOf(Object.values(PARENT_EQUIPMENT_KEY))
    .when('ruleType', {
      is: RULE_TYPES.EQUIPMENT,
      then: string().required('Must specify an equipment type'),
    }),
  countLoadsQuotedCriteria: mixed().when(['ruleType'], {
    is: ruleType => ruleType === RULE_TYPES.STEP_PRICING,
    then: object()
      .shape({
        pickupType: string().required('Must specify a pickup type'),
        deliveryType: string().required('Must specify a delivery type'),
        pickupValue: string().required('Must specify a pickup value'),
        deliveryValue: string().required('Must specify a delivery value'),
        rangeStart: number()
          .transform(value => (Number.isNaN(value) ? undefined : value))
          .nullable(),
        rangeEnd: number()
          .transform(value => (Number.isNaN(value) ? undefined : value))
          .nullable(),
        isSamePickupDelivery: boolean(),
        isDifferentPickupDelivery: boolean(),
      })
      .required('Must specify step pricing details')
      .typeError('Must specify step pricing details'),
    otherwise: object().notRequired(),
  }),
})
